import { useEffect, useState, useCallback } from 'react';
import { IconBadgeListItem } from '@europrocurement/flexy-components/components/molecules/IconBadgeList';
import {
    TopicIdUnread,
    TopicType,
    TopicTypeUnread,
    useWebsocketActions,
    useWebsocketState,
} from '@europrocurement/l2d-websocket';
import { CustomAvatarProps, useAvatarUrl } from '@europrocurement/flexy-components';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { useUser } from '../modules/Utilisateur/hooks/useUser';
import { useTiersOfferContext } from '../modules/Offres/providers/OfferContext';
import { useCurrentPrescriber } from './useCurrentPrescriber';
import { useCurrentContact } from './useCurrentContact';

export enum APIS_SERVICE {
    'COMMUNICATION' = 'service-account-communicationprovider',
}
const useMessagerieNotification = () => {
    const [messageItems, setMessageItems] = useState<
        (IconBadgeListItem & { userIdentifier: string })[]
    >([]);
    const { socketOpen, itemsUnread } = useWebsocketState();
    const { getUnreadItems, setMessagesRead, registerContactId } = useWebsocketActions();
    const [notificationItems, setNotificationItems] = useState<IconBadgeListItem[]>([]);
    const [countNotification, setCountNotification] = useState<number>(0);
    const [countMessage, setCountMessage] = useState<number>(0);
    const { usersAvatarInfos } = useAvatarUrl();
    const { fetchUser, cache } = useUser();
    const { offerDataLoaded, getTiersCurrentOfferWithDefault } = useTiersOfferContext();
    const keycloakService = UseKeycloakService();
    const [utilisateurPremium, setUtilisateurPremium] = useState<boolean>(false);
    const isFormaliste: boolean =
        keycloakService.getTokenParsed()?.realm_access?.roles.includes('interne') ?? false;
    const currPrescriber = useCurrentPrescriber();
    const { getCurrentContactByUserIdentifier } = useCurrentContact();
    const [contactId, setContactId] = useState<number | null>(() => {
        if (isFormaliste) return null;
        const contact = getCurrentContactByUserIdentifier();
        return contact?.id != null ? (contact.id as number) : null;
    });
    useEffect(() => {
        if (isFormaliste) return;
        const contact = getCurrentContactByUserIdentifier();
        if (contact?.id != null && contactId === null) {
            setContactId(contact.id as number);
        }
    }, [getCurrentContactByUserIdentifier, isFormaliste, contactId]);
    useEffect(() => {
        (async () => {
            if (
                !offerDataLoaded ||
                !currPrescriber ||
                currPrescriber.id === undefined ||
                isFormaliste
            ) {
                return;
            }
            const offer = await getTiersCurrentOfferWithDefault(currPrescriber.id);
            setUtilisateurPremium(offer?.type === 'FULL');
        })();
    }, [
        offerDataLoaded,
        isFormaliste,
        currPrescriber?.id,
        currPrescriber,
        getTiersCurrentOfferWithDefault,
    ]);

    useEffect(() => {
        registerContactId(contactId ?? undefined);
    }, [contactId, registerContactId]);

    /**
     * Format WS item → IconBadgeListItem
     */
    const formatItem = useCallback(
        (itemWS: TopicIdUnread): IconBadgeListItem & { userIdentifier: string } => {
            const { count, oldestDocument } = itemWS;
            const { data, messageId } = oldestDocument;
            const detail = data.message;
            // si message migré : nom prénom enregistré dans userIdentifier / sinon Anonyme
            let title = 'Anonyme';
            let message = '';
            let avatar: CustomAvatarProps | undefined = {
                firstName: '',
                lastName: 'Anonyme',
            };
            let link: string | undefined;

            const redirect = data.discriminant?.idDossierFormalite
                ? `/dossiers/${data.discriminant.idDossierFormalite}?dossier-detail=message`
                : '';
            const remove =
                data.topicType && data?.discriminant?.idDossierFormalite
                    ? () => {
                          setMessagesRead({
                              messagesIds: [messageId],
                              topicType: data.topicType as TopicType,
                              topicId: data.topicId,
                          });
                          setMessageItems((prev) => prev.filter((i) => i.id !== messageId));
                          setNotificationItems((prev) => prev.filter((i) => i.id !== messageId));
                      }
                    : null;

            switch (detail.type) {
                case 'message':
                    message = detail.message;
                    break;

                case 'document':
                    message = `Un document vous a été envoyé : ${detail.dosDocFileName}`;
                    link = detail.dosDocFileUrl;
                    break;

                case 'emoji':
                    message = `a réagi à votre message : ${detail.icon}`;
                    break;
                default:
                    message = 'Vous avez un nouveau message';
            }

            const normalized = data.date.replace('+00:00Z', 'Z');
            const date = new Date(normalized).toLocaleString();

            if (data.userIdentifier === APIS_SERVICE.COMMUNICATION) {
                title = 'Système L2D';
                avatar = {
                    firstName: 'Système',
                    lastName: 'L2D',
                };
            } else if (
                data.discriminant?.isMigration &&
                data.userIdentifier.split(' ').length > 1 &&
                (utilisateurPremium || isFormaliste)
            ) {
                title = `${data.userIdentifier.split(' ')[0]} ${data.userIdentifier.split(' ')[1]}`;
                avatar = {
                    firstName: data.userIdentifier.split(' ')[0] ?? '',
                    lastName: data.userIdentifier.split(' ')[1] ?? '',
                };
            } else if (utilisateurPremium || isFormaliste) {
                if (!cache[data.userIdentifier] || cache[data.userIdentifier] === undefined) {
                    fetchUser(data.userIdentifier);
                }
                const user = cache[data.userIdentifier];
                if (user != null) {
                    title = `${user.firstName} ${user.lastName}`;
                    if (title.length > 15) {
                        title = `${title.substring(0, 12)}...`;
                    }
                    avatar = {
                        firstName: user.firstName ?? '',
                        lastName: user.lastName ?? '',
                        src: usersAvatarInfos?.find(
                            (el) => el.avatarUserIdentifier === user.username,
                        )?.currentUserAvatarUrl,
                        keycloakId: user.username,
                    };
                }
            }

            return {
                id: messageId,
                title,
                message,
                avatar,
                link,
                date,
                redirect,
                userIdentifier: data.userIdentifier,
                count,
                remove,
                tooltipAction: 'Marquer comme lu',
            } as IconBadgeListItem & { userIdentifier: string };
        },
        [cache, fetchUser, usersAvatarInfos, setMessagesRead, isFormaliste, utilisateurPremium],
    );

    /**
     * Init socket + unread fetch
     */
    useEffect(() => {
        if (socketOpen) {
            if (contactId == null) {
                getUnreadItems();
            } else {
                getUnreadItems(contactId ?? null);
            }
        }
    }, [socketOpen, getUnreadItems, contactId]);

    /**
     * Notifications / Message unread
     */
    useEffect(() => {
        if (!itemsUnread) {
            return;
        }

        const notification = itemsUnread.topicTypes.find(
            (item: TopicTypeUnread) => item.topicType === 'notification',
        );

        const message = itemsUnread.topicTypes.find(
            (item: TopicTypeUnread) => item.topicType === 'messagerie_dossier',
        );

        if (notification && notification?.topics) {
            setCountNotification(notification.count);
            setNotificationItems(notification.topics.map(formatItem));
        }
        if (message && message?.topics) {
            setCountMessage(message.count);
            setMessageItems(message.topics.map(formatItem));
        }
    }, [itemsUnread, formatItem]);

    /**
     * Update avatars once cache is filled
     */
    useEffect(() => {
        if (!cache || messageItems.length === 0) return;

        let hasUpdate = false;

        const updated = messageItems.map((item) => {
            const user = cache[item.userIdentifier];
            if (!user) return item;
            if (item.title !== 'Anonyme') return item;

            hasUpdate = true;

            return {
                ...item,
                title: `${user?.firstName} ${user?.lastName}`,
                avatar: {
                    firstName: user.firstName ?? 'Anonyme',
                    lastName: user.lastName ?? '',
                    src: usersAvatarInfos?.find((el) => el.avatarUserIdentifier === user.username)
                        ?.currentUserAvatarUrl,
                    keycloakId: user.username,
                },
            };
        });

        if (hasUpdate) {
            setMessageItems(updated);
        }
    }, [cache, messageItems, usersAvatarInfos]);

    return {
        messageItems,
        notificationItems,
        countNotification,
        countMessage,
    };
};

export default useMessagerieNotification;
